/* eslint-disable react/no-children-prop */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Granite Bagas
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';

import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import * as Layout from '@components/screen/Layout';
import StatusPicker from '@components/form/StatusPicker';
import StatusBadge from '@components/global/StatusBadge';
import Image from '@components/global/Image';

import { isWeb, fullWidth } from '@global/screens';
import Colors from '@global/colors';
import { currency, parseDate, formatSkpType } from '@global/helpers';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { paramsReseted, filterAdded } from '@/store/params';
import { showPopup } from '@/store/modal';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetSkpExternalRelatedListQuery,
  useGetSkpExternalRelatedCountQuery,
  useGetSkpExternalRelatedInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructureSkpInternal = {
  skp_reference: { head: 'SKP Name', body: 'name' },
  reference_code: { head: 'SKP number', body: 'reference_code' },
  cost_type: { head: 'COA', body: 'coa_tax.cost_type' },
  skp_type: { head: 'Type', body: 'skp_type' },
  period: { head: 'Periode', body: 'period' },
  total_estimate_budget: {
    head: 'Total Estimate Budget',
    body: 'total_estimate_budget',
  },
  total_actual_budget: {
    head: 'Total Actual Budget',
    body: 'total_actual_budget',
  },
  budget_used: { head: 'Budget Used', body: 'budget_used' },
  budget_remaining: { head: 'Budget Remaining', body: 'budget_remaining' },
  status: { head: 'Status', body: 'status', type: 'status' },
  is_active: { head: 'Is Active', body: 'is_active', type: 'status' },
  last_updated_by: { head: 'Last Updated, By', body: 'last_updated_by' },
};

const statuses = [
  { name: 'Approved', value: 'approved' },
  { name: 'Rejected', value: 'rejected' },
  { name: 'Draft', value: 'draft' },
  { name: 'Pending Approval', value: 'need_approval' },
  { name: 'Joined', value: 'joined' },
  { name: 'Cancelled', value: 'cancelled' },
  { name: 'Completed', value: 'completed' },
];

const TABS = [
  { label: 'Internal', value: 'internal' },
  { label: 'External', value: 'external' },
];

// Display row (RTK cache frozen → new object): period, last-updated, skp_type.
const mapRow = (item) => {
  const next = { ...item, id: item._id };
  if (item.from_date && item.to_date) {
    next.period = `${parseDate(item.from_date)} - ${parseDate(item.to_date)}`;
  }
  const lastUpdatedAt = item.updated_at ? parseDate(item.updated_at) : '-';
  const updatedBy = item.updated_by?.name || '-';
  next.last_updated_by = `${lastUpdatedAt}, ${updatedBy}`;
  next.skp_type = formatSkpType(item.skp_internal?.skp_type);
  return next;
};

const SkpExternalListScreen = (props) => {
  const dispatch = useDispatch();
  const showFilter = useSelector((state) => state.modal.showFilter);

  const parentId = props?.route?.params?.id;
  const isFromExternal = props?.route?.params?.isFromExternal ? true : false;
  const skpExternalId = props?.route?.params?.skpExternalId;
  const customerId = props?.route?.params?.customerId;

  const [detailSkpInternal, setDetailSkpInternal] = useState({});
  const [activeTab, setActiveTab] = useState('internal');
  const [draftFilter, setDraftFilter] = useState({});

  const getSkpInternalDetail = useApi(MsOrder.getSkpInternalDetail);

  // Parent context (skp_internal.id [+ customer.id]) is injected into every
  // query; keyword → $or; status passthrough.
  const toApiFilter = (raw = {}) => {
    const filter = { 'skp_internal.id': parentId };
    if (isFromExternal) {
      filter['customer.id'] = customerId;
    }
    const keyword = raw.keyword?.$regex ?? raw.keyword;
    if (keyword) {
      filter['$or'] = [
        { 'customer.code': { $regex: keyword, $options: 'i' } },
        { 'customer.name': { $regex: keyword, $options: 'i' } },
        { 'outlet.code': { $regex: keyword, $options: 'i' } },
        { 'outlet.name': { $regex: keyword, $options: 'i' } },
        { reference_code: { $regex: keyword, $options: 'i' } },
      ];
    }
    if (raw.status) {
      filter.status = raw.status;
    }
    return filter;
  };

  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetSkpExternalRelatedListQuery,
    countHook: useGetSkpExternalRelatedCountQuery,
    infiniteHook: useGetSkpExternalRelatedInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
    // Per-parent scope so different internal SKPs don't share search/status.
    scopeKey: `SkpExternalRelated:${parentId}`,
  });

  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  const tableStructure = {
    reference_code: { head: 'SKP number', body: 'reference_code' },
    skp_type: { head: 'Type', body: 'skp_type' },
    sales_team: { head: 'Sales Team', body: 'sales_team.name' },
    name: { head: 'Customer Name', body: 'customer.name' },
    ...(detailSkpInternal?.skp_type?.toLowerCase() == 'subdistributor'
      ? { outlet_name: { head: 'Outlet Name', body: 'outlet.name' } }
      : {}),
    claim: { head: 'Claim', body: 'claim_type', type: 'status' },
    skp_balance: {
      head: 'SKP Balance Remaining',
      body: 'skp_balance',
      type: 'currency',
    },
    total_biaya: {
      head: 'Total Biaya/DPP(coa exp)',
      body: 'total_biaya',
      type: 'currency',
    },
    pph: { head: 'PPH(coa wht)', body: 'pph', type: 'currency' },
    ppn: { head: 'PPN(coa wht)', body: 'ppn', type: 'currency' },
    total_nett: {
      head: 'Total Nett(coa ar)',
      body: 'total_nett',
      type: 'currency',
    },
    status: { head: 'Status', body: 'status', type: 'status' },
    last_updated_by: { head: 'Last Updated, By', body: 'last_updated_by' },
  };

  const getSkpInternalDetails = async (id) => {
    const response = await getSkpInternalDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      const mt = result.market_type?.code || '';
      result.skp_type = result.skp_type || mt.toLowerCase();
      result.is_active = result.is_active ? 'active' : 'in_active';
      if (result.from_date && result.to_date) {
        result.period = `${parseDate(result.from_date)} - ${parseDate(
          result.to_date,
        )}`;
      }
      const lastUpdatedAt = result.updated_at
        ? parseDate(result.updated_at)
        : '-';
      const updatedBy = result.updated_by?.name || '-';
      result.last_updated_by = `${lastUpdatedAt}, ${updatedBy}`;
      result.budget_remaining = currency(result.budget_remaining, true);
      result.total_estimate_budget = currency(
        result.total_estimate_budget,
        true,
      );
      result.total_actual_budget = currency(result.total_actual_budget, true);
      result.budget_used = currency(result.budget_used, true);
      result.skp_type = formatSkpType(result.skp_type);
      setDetailSkpInternal(result);
    }
  };

  useEffect(() => {
    if (parentId) {
      getSkpInternalDetails(parentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  // Seed the staged status filter from the applied filter when the modal opens.
  useEffect(() => {
    if (showFilter) {
      setDraftFilter(params.filter || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  const goBack = () => {
    if (isFromExternal) {
      props.navigation.navigate('SkpExternalDetailScreen', {
        id: skpExternalId,
      });
    } else {
      props.navigation.navigate('SkpInternalDetailScreen', { id: parentId });
    }
  };

  const goToDetail = (skpType = 'external', data) => {
    if (skpType == 'external') {
      const fromPromotionAgreementExternal = !isFromExternal;
      openChild('SkpExternalNavigator', {
        screen: 'SkpExternalDetailScreen',
        params: {
          id: data._id,
          ...(fromPromotionAgreementExternal && {
            fromPromotionAgreementExternalList: true,
          }),
        },
      });
    } else {
      openChild('SkpInternalNavigator', {
        screen: 'SkpInternalDetailScreen',
        params: { id: data._id },
      });
    }
  };

  const TableMobileExternal = () => (
    <TableMobile
      testID="skp-external-related-list-table-mobile"
      filterName={scope}
      searchKey="keyword"
      loading={list.isLoading}
      refetching={list.isFetching}
      loadingLoadMore={list.isFetchingNextPage}
      hasNextPage={list.hasNextPage}
      onLoadMore={list.fetchNextPage}
      onRefresh={list.refetch}
      showSearch={true}
      skip={params.skip}
      limit={params.limit}
      data={rows}
      totalData={count.data}
      searchValue={searchValue}
      renderItem={({ data, index }) => (
        <TouchableOpacity
          testID={`skp-external-related-list-item-${index}`}
          activeOpacity={0.6}
          style={styles.skpItem}
          key={index}
          onPress={() => goToDetail('external', data)}>
          <View
            style={[
              styles.skpItemSection,
              { alignItems: 'flex-start', marginBottom: 5 },
            ]}>
            <SociollaText
              type="medium"
              style={{ fontSize: 15, width: fullWidth - 140 }}>
              {data.reference_code ? data.reference_code : '-'}
            </SociollaText>
            <StatusBadge status={data.status} />
          </View>
          <View style={[styles.skpItemSection, { alignItems: 'flex-start' }]}>
            <SociollaText
              style={{
                marginTop: 12,
                marginBottom: 10,
                fontSize: 14,
                color: Colors.grey2,
              }}>
              {data.skp_internal?.reference_code
                ? data.skp_internal.reference_code
                : '-'}
            </SociollaText>
            <StatusBadge status={data.claim_type} />
          </View>

          <View style={styles.skpItemSection}>
            <SociollaText style={{ fontSize: 12, color: Colors.grey2 }}>
              {data.sales_team?.name ? data.sales_team.name : '-'}
            </SociollaText>
            <SociollaText
              type="medium"
              style={{ fontSize: 14, color: Colors.blue1 }}>
              {currency(data.total_nett, true)}
            </SociollaText>
          </View>
        </TouchableOpacity>
      )}
    />
  );

  const TableMobileInternal = () => (
    <TableMobile
      searchKey="keyword"
      loading={getSkpInternalDetail.loading}
      tableData={[detailSkpInternal]}
      showSearch={false}
      withLoadMore={false}
      withFetch={false}
      noResetOnRefresh
      renderItem={({ data, index }) => (
        <TouchableOpacity
          activeOpacity={0.6}
          style={styles.skpItem}
          key={index}
          onPress={() => goToDetail('internal', data)}>
          <View
            style={[
              styles.skpItemSectionInternal,
              { alignItems: 'flex-start' },
            ]}>
            <SociollaText
              type="medium"
              style={{ fontSize: 15, width: fullWidth - 140, marginTop: 1 }}>
              {data.name ? data.name : '-'}
            </SociollaText>
            <StatusBadge status={data.status} />
          </View>
          <View
            style={[
              styles.skpItemSectionInternal,
              { alignItems: 'flex-start', marginBottom: 4 },
            ]}>
            <SociollaText
              style={{
                marginTop: 12,
                marginBottom: 10,
                fontSize: 14,
                color: Colors.grey2,
              }}>
              {data.reference_code ? data.reference_code : '-'}
            </SociollaText>
            <SociollaText
              type="medium"
              style={{
                marginTop: 13,
                marginBottom: 10,
                fontSize: 12,
                color: Colors.blue1,
              }}>
              {data.total_estimate_budget}
            </SociollaText>
          </View>
          <View
            style={[
              styles.skpItemSectionInternal,
              { alignItems: 'flex-start', marginBottom: 4 },
            ]}>
            <View style={styles.skpItemSectionInternal}>
              <View style={{ flexDirection: 'row' }}>
                <Image
                  style={{ width: 20, height: 20, bottom: 3, marginRight: 5 }}
                  svg={true}
                  height={20}
                  width={20}
                  source={require('@assets/img/icons/menu/calendar.svg')}
                />
                <SociollaText style={{ fontSize: 10, color: Colors.grey2 }}>
                  {data.period ? data.period : '-'}
                </SociollaText>
              </View>
            </View>
            <SociollaText
              type="medium"
              style={{ marginBottom: 10, fontSize: 12, color: Colors.blue1 }}>
              {data?.budget_remaining ? data?.budget_remaining : '-'}
            </SociollaText>
          </View>
        </TouchableOpacity>
      )}
    />
  );

  return (
    <Layout.Container testID="skp-external-related-list-screen">
      {isWeb ? (
        <Layout.Wrapper white={false}>
          <Layout.Back
            testID="skp-external-related-list-back"
            title={`Promotion Agreement ${
              isFromExternal ? '' : detailSkpInternal?.name
            }`}
            onCallback={() => goBack()}
          />
        </Layout.Wrapper>
      ) : (
        false
      )}

      {isWeb && isFromExternal ? (
        <View style={{ marginBottom: 50 }}>
          <Table
            testID="skp-external-related-list-internal-table"
            name="skp-internal-detail"
            structures={tableStructureSkpInternal}
            data={[detailSkpInternal]}
            hasNoHide={true}
            hasCallback={true}
            showFilter={false}
            showSearch={false}
            showPagination={false}
            onClickRow={({ data }) => goToDetail('internal', data)}
          />
        </View>
      ) : (
        false
      )}

      {isWeb ? (
        <Table
          testID="skp-external-related-list-table"
          filterName={scope}
          searchKey="keyword"
          name="skp-external-list"
          structures={tableStructure}
          data={rows}
          loading={list.isLoading}
          refetching={list.isFetching}
          hasNoHide={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          hasCallback={true}
          showFilter={!isFromExternal}
          showSearch={!isFromExternal}
          searchPlaceholder={
            'Search by SKP number, Customer Name, Customer Code, Outlet Name, Outlet Code, Kwitansi Number, SKP External Account Number...'
          }
          searchValue={searchValue}
          onClickRow={({ data }) => goToDetail('external', data)}
        />
      ) : (
        false
      )}

      {!isWeb && isFromExternal ? (
        <View style={styles.tabContainer}>
          {TABS.map((item, idx) => (
            <TouchableOpacity
              testID={`skp-external-related-list-tab-${item.value}`}
              style={[
                styles.tabItem,
                item.value == activeTab ? styles.tabItemActive : {},
              ]}
              activeOpacity={0.8}
              onPress={() => setActiveTab(item.value)}
              key={idx}>
              <SociollaText
                type={item.value == activeTab ? 'bold' : 'medium'}
                style={[
                  styles.tabItemText,
                  item.value == activeTab ? styles.tabItemTextActive : {},
                ]}>
                {item.label}
              </SociollaText>
            </TouchableOpacity>
          ))}
        </View>
      ) : (
        false
      )}

      {/* Rendered as function CALLS (not <TableMobileExternal/>) so the
          TableMobile subtree keeps a stable identity across parent re-renders —
          otherwise the inline component type changes every render, remounting
          the list (scroll jumps to top + load-more guard resets). */}
      {!isWeb && isFromExternal && activeTab == 'internal'
        ? TableMobileInternal()
        : null}

      {!isWeb && (!isFromExternal || activeTab == 'external')
        ? TableMobileExternal()
        : null}

      {/* Filter Container */}
      <Layout.Filter
        testID="skp-external-related-list-filter"
        name="skp-external-list"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Status
          </SociollaText>
          <StatusPicker
            testID="skp-external-related-list-filter-status"
            label=""
            options={statuses}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, status: val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  skpItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  skpItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: fullWidth - 20,
  },
  skpItemSectionInternal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tabContainer: {
    width: '100%',
    paddingVertical: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: Colors.white,
    borderRadius: 5,
    zIndex: 10,
  },
  tabItem: {
    width: '50%',
    paddingVertical: 10,
    borderBottomColor: Colors.grey7,
    borderBottomWidth: 2,
  },
  tabItemActive: {
    borderBottomColor: Colors.blue0,
  },
  tabItemText: {
    fontSize: 13,
    color: Colors.grey1,
    textAlign: 'center',
  },
  tabItemTextActive: {
    color: Colors.black0,
  },
});

export default SkpExternalListScreen;
